import {
  SOLUTION_DENSITY_G_PER_ML,
  WATER_SPECIFIC_HEAT_J_PER_G_K,
  formatDecimalSigned,
  formatSig,
  type ChangeType,
  type EnergyDirection,
} from './energyCalorimetry';
import { DILUTE_LIMIT_MOL_PER_L, LIQUID_RANGE_C } from './calorimetryExplore';
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';

/**
 * Year 8 Explore of the Energy & Calorimetry Lab: "Hot pack and cold pack lab" (study-chemistry-tools-prd.md
 * v1.12). The learner chooses a substance, its mass, the water volume and the starting temperature; the
 * model shares the energy released or taken in through the water and works out the temperature change.
 * Year 8 does not use moles, so each substance carries an energy per gram (|ΔH| ÷ M).
 */
export interface PackSubstance {
  id: string;
  name: string;
  equation: string;
  changeType: ChangeType;
  direction: EnergyDirection;
  /** Reviewed enthalpy change per mole of the substance, kJ/mol (negative = releases energy). */
  deltaHkJPerMol: number;
  /** Molar mass, g/mol (IUPAC standard atomic weights). */
  molarMassGPerMol: number;
  everyday: string;
  source: string;
}

const CRC_SOLUTION = 'CRC Handbook of Chemistry and Physics, enthalpy of solution of electrolytes at 25 °C (infinite dilution)';

/** reviewStatus: 'pending-teaching-review', like the other reviewed values in this tool. */
export const PACK_SUBSTANCES: PackSubstance[] = [
  {
    id: 'calcium-chloride',
    name: 'Calcium chloride',
    equation: 'CaCl₂(s) → Ca²⁺(aq) + 2Cl⁻(aq)',
    changeType: 'physical',
    direction: 'exothermic',
    deltaHkJPerMol: -81.3,
    molarMassGPerMol: 110.98,
    everyday: 'Some instant hot packs',
    source: CRC_SOLUTION,
  },
  {
    id: 'magnesium-acid',
    name: 'Magnesium + hydrochloric acid',
    equation: 'Mg(s) + 2HCl(aq) → MgCl₂(aq) + H₂(g)',
    changeType: 'chemical',
    direction: 'exothermic',
    deltaHkJPerMol: -466.9,
    molarMassGPerMol: 24.305,
    everyday: 'A school practical: the test tube gets warm',
    source: 'Derived from NIST/CODATA ΔfH°(Mg²⁺, aq) = −466.9 kJ/mol; acid in excess',
  },
  {
    id: 'ammonium-nitrate',
    name: 'Ammonium nitrate',
    equation: 'NH₄NO₃(s) → NH₄⁺(aq) + NO₃⁻(aq)',
    changeType: 'physical',
    direction: 'endothermic',
    deltaHkJPerMol: 25.7,
    molarMassGPerMol: 80.04,
    everyday: 'Instant cold packs for sports injuries',
    source: CRC_SOLUTION,
  },
  {
    id: 'potassium-nitrate',
    name: 'Potassium nitrate',
    equation: 'KNO₃(s) → K⁺(aq) + NO₃⁻(aq)',
    changeType: 'physical',
    direction: 'endothermic',
    deltaHkJPerMol: 34.9,
    molarMassGPerMol: 101.1,
    everyday: 'Takes in energy as it dissolves',
    source: CRC_SOLUTION,
  },
];

export interface PackSetup {
  substanceId: string;
  massG: number;
  waterMl: number;
  startC: number;
}

export type PackInput = 'massG' | 'waterMl' | 'startC';

export const PACK_FIELDS: ModelField<PackInput>[] = [
  { key: 'massG', label: 'Mass of substance', unit: 'g', min: 0.1, max: 50, step: 0.1 },
  { key: 'waterMl', label: 'Water', unit: 'mL', min: 20, max: 500, step: 1 },
  { key: 'startC', label: 'Starting temperature', unit: '°C', min: 5, max: 40, step: 0.5 },
];

export const DEFAULT_PACK_SETUP: PackSetup = { substanceId: 'calcium-chloride', massG: 5, waterMl: 100, startC: 20 };

const JOULES_PER_KILOJOULE = 1000;
const MILLILITRES_PER_LITRE = 1000;

export const PACK_ASSUMPTIONS =
  'Model assumptions: water has density 1.00 g/mL and c = 4.18 J/(g·°C); all the energy goes into or comes out of the water (no heat lost to the cup or air); for magnesium, there is enough acid for all of it to react.';

export type PackLimit = 'phase-change' | 'concentrated';

export interface PackExploreResult {
  substance: PackSubstance;
  massG: number;
  waterMl: number;
  startC: number;
  energyPerGramKJ: number;
  /** Size of the energy released or taken in, kJ (always positive). */
  energyKJ: number;
  direction: EnergyDirection;
  waterMassG: number;
  /** Null when the model stops at a limit. */
  deltaTC: number | null;
  finalC: number | null;
  concentrationMolPerL: number;
  limit: PackLimit | null;
  steps: WorkedStep[];
  result: string;
  limitNote: string;
}

export function packSubstance(id: string): PackSubstance {
  const substance = PACK_SUBSTANCES.find((entry) => entry.id === id);
  if (!substance) throw new Error(`Unknown substance: ${id}`);
  return substance;
}

/** Energy per gram, kJ/g: |ΔH| ÷ M. */
export function energyPerGram(substance: PackSubstance): number {
  return Math.abs(substance.deltaHkJPerMol) / substance.molarMassGPerMol;
}

/** Worked hot/cold pack model for one learner setup. Throws on invalid input (fail closed). */
export function calculatePackExplore(setup: PackSetup): PackExploreResult {
  const substance = packSubstance(setup.substanceId);
  const { massG, waterMl, startC } = setup;
  const error = firstModelError(PACK_FIELDS, { massG, waterMl, startC });
  if (error) throw new Error(error);

  const perGram = energyPerGram(substance);
  const energyKJ = massG * perGram;
  const direction = substance.direction;
  const sign = direction === 'exothermic' ? 1 : -1;
  const waterMassG = waterMl * SOLUTION_DENSITY_G_PER_ML;
  const concentrationMolPerL = massG / substance.molarMassGPerMol / (waterMl / MILLILITRES_PER_LITRE);
  const rawDeltaT = (sign * energyKJ * JOULES_PER_KILOJOULE) / (waterMassG * WATER_SPECIFIC_HEAT_J_PER_G_K);
  const rawFinal = startC + rawDeltaT;
  const limit: PackLimit | null =
    concentrationMolPerL > DILUTE_LIMIT_MOL_PER_L
      ? 'concentrated'
      : rawFinal <= LIQUID_RANGE_C.min || rawFinal >= LIQUID_RANGE_C.max
        ? 'phase-change'
        : null;
  const deltaTC = limit ? null : rawDeltaT;
  const finalC = limit ? null : rawFinal;
  const flow = direction === 'exothermic' ? 'released into the water' : 'taken in from the water';

  const steps: WorkedStep[] = [
    {
      id: 'energy',
      title: direction === 'exothermic' ? 'Energy released' : 'Energy taken in',
      formula: 'energy = mass × energy per gram',
      working: `energy = ${formatModelNumber(massG, 1)} g × ${formatSig(perGram, 3)} kJ/g = ${formatSig(energyKJ, 3)} kJ`,
      status: 'done',
      note: flow,
    },
    {
      id: 'water',
      title: 'Mass of water',
      formula: 'm = V × 1.00 g/mL',
      working: `m = ${formatModelNumber(waterMl, 0)} mL × ${SOLUTION_DENSITY_G_PER_ML.toFixed(2)} g/mL = ${formatModelNumber(waterMassG, 0)} g`,
      status: 'done',
    },
    {
      id: 'delta-t',
      title: 'Temperature change',
      formula: 'ΔT = energy ÷ (m × 4.18)',
      working:
        limit === 'concentrated'
          ? ''
          : `ΔT = ${sign > 0 ? '+' : '−'}${formatModelNumber(energyKJ * JOULES_PER_KILOJOULE, 0)} J ÷ (${formatModelNumber(waterMassG, 0)} g × ${WATER_SPECIFIC_HEAT_J_PER_G_K} J/(g·°C)) = ${formatDecimalSigned(rawDeltaT, 1)} °C`,
      status: limit === 'concentrated' ? 'not-reached' : 'done',
      note: limit === 'concentrated' ? 'stopped: solution too concentrated' : direction === 'exothermic' ? 'water warms' : 'water cools',
    },
    {
      id: 'final',
      title: 'Final temperature',
      formula: 'T(final) = T(start) + ΔT',
      working: finalC === null || deltaTC === null ? '' : `T = ${startC.toFixed(1)} °C ${deltaTC >= 0 ? '+' : '−'} ${Math.abs(deltaTC).toFixed(1)} °C = ${finalC.toFixed(1)} °C`,
      status: finalC === null ? 'not-reached' : 'done',
      note: limit === 'phase-change' ? 'stopped: water would freeze or boil' : undefined,
    },
  ];

  let result: string;
  let limitMessage = '';
  if (limit === 'concentrated') {
    result = `Stopped: ${formatSig(concentrationMolPerL, 3)} mol/L is too concentrated for this model`;
    limitMessage = `${formatModelNumber(massG, 1)} g in ${formatModelNumber(waterMl, 0)} mL is too much substance for this water. Add more water or use less substance. `;
  } else if (limit === 'phase-change') {
    result = `Stopped: the water would reach ${rawFinal >= LIQUID_RANGE_C.max ? '100 °C and boil' : '0 °C and freeze'}`;
    limitMessage =
      'At 0 °C the water starts to freeze and at 100 °C it starts to boil, so the temperature stops changing in a simple way. The model stops here instead of guessing. ';
  } else {
    result = `${formatSig(energyKJ, 3)} kJ ${flow} · ${startC.toFixed(1)} °C → ${(finalC ?? startC).toFixed(1)} °C (ΔT = ${formatDecimalSigned(deltaTC ?? 0, 1)} °C)`;
  }

  return {
    substance,
    massG,
    waterMl,
    startC,
    energyPerGramKJ: perGram,
    energyKJ,
    direction,
    waterMassG,
    deltaTC,
    finalC,
    concentrationMolPerL,
    limit,
    steps,
    result,
    limitNote: `${limitMessage}${PACK_ASSUMPTIONS}`,
  };
}

export const PACK_QUESTION = 'How can a thermometer tell you whether a change releases energy or takes it in?';

const HOT_PACK_RISE_C = 10;
const COLD_PACK_BELOW_C = 10;
const MORE_WATER_ML = 250;
const DEFAULT_MASS_G = DEFAULT_PACK_SETUP.massG;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the model result. Nothing ticks at the default. */
export const PACK_CHALLENGES: ExploreChallenge<PackExploreResult>[] = [
  {
    id: 'hot-pack',
    prompt: 'Make a hot pack that warms the water by at least 10 °C.',
    hint: 'Choose a substance that releases energy, then change how much of it you use.',
    isMet: (result) => result.limit === null && result.direction === 'exothermic' && (result.deltaTC ?? 0) >= HOT_PACK_RISE_C,
    why: 'More substance releases more energy into the same water, so the thermometer rises further. A rising thermometer means energy went out of the chemicals into the water: exothermic.',
  },
  {
    id: 'cold-pack',
    prompt: 'Make a cold pack: get the water below 10 °C.',
    hint: 'Choose a substance that takes energy in as it dissolves. How much do you need?',
    isMet: (result) =>
      result.limit === null && result.direction === 'endothermic' && result.finalC !== null && result.finalC < COLD_PACK_BELOW_C,
    why: 'Dissolving ammonium nitrate or potassium nitrate takes energy from the water, so the water cools. A falling thermometer means energy went into the chemicals: endothermic.',
  },
  {
    id: 'more-water',
    prompt: 'Keep 5 g of calcium chloride, but use 250 mL of water or more. Why is the rise smaller?',
    hint: 'Choose calcium chloride, keep the mass at 5 g and change only the water.',
    isMet: (result) =>
      result.limit === null &&
      result.substance.id === 'calcium-chloride' &&
      result.massG === DEFAULT_MASS_G &&
      result.waterMl >= MORE_WATER_ML,
    why: 'The same 5 g releases the same 3.66 kJ, but now it is shared by more water, so each gram of water warms less. Energy and temperature are not the same thing.',
  },
];
